/**
 * ============================================================================
 * QR CODE COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Displays QR code for transaction links
 */

import React, { useState, useEffect } from 'react';
import { useLinks } from '../../hooks/useLinks';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Spinner from '../common/Spinner';

/**
 * QR Code Component
 * @param {Object} props
 * @param {string} props.transactionId - Transaction ID
 * @param {string} props.shortCode - Short code (optional)
 * @param {string} props.url - Direct URL (optional)
 * @param {number} props.size - QR code size (default: 200)
 * @returns {JSX.Element}
 */
const QRCode = ({ transactionId, shortCode, url, size = 200 }) => {
  const { getQRCode, loading } = useLinks();
  const { showError } = useToast();
  const { language } = useI18n();
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadQRCode = async () => {
      if (url) {
        // Generate QR code from URL directly
        try {
          const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}`;
          setQrCodeUrl(qrUrl);
          setIsLoading(false);
        } catch (error) {
          showError(error.message || (language === 'fr' ? 'Échec de génération du QR code' : 'Failed to generate QR code'));
          setIsLoading(false);
        }
      } else if (transactionId || shortCode) {
        // Get QR code from API
        try {
          setIsLoading(true);
          const qrData = await getQRCode(transactionId || shortCode);
          setQrCodeUrl(qrData.qrCodeUrl || qrData.url || qrData);
        } catch (error) {
          showError(error.message || (language === 'fr' ? 'Échec du chargement du QR code' : 'Failed to load QR code'));
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    loadQRCode();
  }, [transactionId, shortCode, url, size, getQRCode, showError, language]);

  const handleDownload = () => {
    if (!qrCodeUrl) return;
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `qr-code-${transactionId || shortCode || 'transaction'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading || loading) {
    return (
      <Card variant="glass" className="p-12 text-center">
        <Spinner size="md" />
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
          {language === 'fr' ? 'Génération du QR code...' : 'Generating QR code...'}
        </p>
      </Card>
    );
  }

  if (!qrCodeUrl) {
    return (
      <Card variant="glass" className="p-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">
          {language === 'fr' ? 'QR code non disponible' : 'QR code not available'}
        </p>
      </Card>
    );
  }

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <div className="space-y-4">
        <h3 className="text-lg font-black text-gray-900 dark:text-white flex items-center gap-2">
          <span className="text-2xl">📱</span>
          {language === 'fr' ? 'Code QR' : 'QR Code'}
        </h3>
        <div className="flex flex-col items-center justify-center p-6 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700">
          <img
            src={qrCodeUrl}
            alt="QR Code"
            className="w-full max-w-xs h-auto rounded-lg shadow-lg"
            style={{ maxWidth: `${size}px` }}
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            fullWidth
            onClick={handleDownload}
            className="flex items-center justify-center gap-2"
          >
            <span>💾</span>
            {language === 'fr' ? 'Télécharger' : 'Download'}
          </Button>
          <Button
            variant="gradient"
            fullWidth
            onClick={() => {
              const link = document.createElement('a');
              link.href = qrCodeUrl;
              link.target = '_blank';
              link.rel = 'noopener noreferrer';
              link.click();
            }}
            className="flex items-center justify-center gap-2"
          >
            <span>🔗</span>
            {language === 'fr' ? 'Ouvrir' : 'Open'}
          </Button>
        </div>
        <p className="text-xs text-center text-gray-500 dark:text-gray-400">
          {language === 'fr'
            ? 'Scannez avec votre téléphone pour accéder à la transaction'
            : 'Scan with your phone to access the transaction'}
        </p>
      </div>
    </Card>
  );
};

export default QRCode;
